// v2/components/MobileNav.js — mobil alt cubuk + sagdan acilan menu
import React, { useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home, Boxes, ShoppingCart, Menu, X,
  Sparkles, User, ClipboardList, CreditCard, Wallet, FileText, LogOut,
} from 'lucide-react';
import { BOTTOM_NAV, DRAWER_MENU } from '../constants/nav';
import styles from './MobileNav.module.css';

const ICONS = {
  Home, Boxes, ShoppingCart, Menu,
  Sparkles, User, ClipboardList, CreditCard, Wallet, FileText, LogOut,
};

export default function MobileNav({
  cartCount = 0,
  customerName = '', customerTitle = '', customerCode = '',
  open = false, onOpenChange = () => {}, onSelect = () => {},
}) {
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const { pathname } = useLocation();

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  // ESC + arka plan kilidi + acilista odak
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', esc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => { if (closeRef.current) closeRef.current.focus(); }, 40);
    return () => {
      document.removeEventListener('keydown', esc);
      document.body.style.overflow = prev;
      clearTimeout(t);
    };
  }, [open, close]);

  // Tab dongusu
  const onKeyDown = (e) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const items = panelRef.current.querySelectorAll(
      'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const badgeFor = (kind) => (kind === 'cart' ? cartCount : 0);
  const initial = (customerName || '?').trim().charAt(0).toUpperCase();

  return (
    <>
      <nav className={styles.bar} aria-label="Alt menü">
        {BOTTOM_NAV.map((item) => {
          const Icon = ICONS[item.icon] || Home;
          const n = badgeFor(item.badge);
          const isOn = item.to ? pathname === item.to : (item.action === 'drawer' && open);

          const inner = (
            <>
              <span className={styles.barIconWrap}>
                <Icon size={21} strokeWidth={1.8} />
                {n > 0 && <span className={styles.barBadge}>{n > 99 ? '99+' : n}</span>}
              </span>
              <span className={styles.barLabel}>{item.label}</span>
            </>
          );

          if (item.to) {
            return (
              <Link key={item.id} to={item.to}
                    className={`${styles.barItem} ${isOn ? styles.barOn : ''}`}>
                {inner}
              </Link>
            );
          }
          return (
            <button
              key={item.id} type="button"
              className={`${styles.barItem} ${isOn ? styles.barOn : ''}`}
              onClick={() => (item.action === 'drawer' ? onOpenChange(!open) : onSelect(item.id))}
              aria-expanded={item.action === 'drawer' ? open : undefined}
            >
              {inner}
            </button>
          );
        })}
      </nav>

      <div
        className={`${styles.scrim} ${open ? styles.scrimOn : ''}`}
        onClick={close} aria-hidden="true"
      />

      <aside
        ref={panelRef} onKeyDown={onKeyDown}
        className={`${styles.drawer} ${open ? styles.drawerOn : ''}`}
        role="dialog" aria-modal="true" aria-label="Menü"
        aria-hidden={!open}
      >
        <div className={styles.drawerHead}>
          <span className={styles.drawerAvatar}>{initial}</span>
          <span className={styles.drawerUser}>
            <span className={styles.drawerName}>{customerName}</span>
            {customerTitle && <span className={styles.drawerTitle}>{customerTitle}</span>}
            {customerCode && <span className={styles.drawerCode}>Cari Kod: {customerCode}</span>}
          </span>
          <button ref={closeRef} type="button" className={styles.drawerClose}
                  onClick={close} aria-label="Kapat" tabIndex={open ? 0 : -1}>
            <X size={18} />
          </button>
        </div>

        <div className={styles.drawerList}>
          {DRAWER_MENU.map((item, i) => {
            const Icon = ICONS[item.icon] || User;
            const n = badgeFor(item.badge);
            return (
              <button
                key={item.id} type="button"
                className={`${styles.drawerItem} ${item.danger ? styles.drawerDanger : ''} ${item.accent ? styles.drawerAccent : ''}`}
                style={{ transitionDelay: open ? `${40 + i * 26}ms` : '0ms' }}
                onClick={() => { close(); onSelect(item.id); }}
                tabIndex={open ? 0 : -1}
              >
                <span className={styles.drawerIcon}><Icon size={18} strokeWidth={1.8} /></span>
                {item.label}
                {n > 0 && <span className={styles.drawerBadge}>{n}</span>}
              </button>
            );
          })}
        </div>
      </aside>
    </>
  );
}
